import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import Profile from '../../../assets/img/User.png';
import {
  AdminPanelTitle,
  AdminAlertText,
  AdminHeaderAction,
  AdminStatusBadge,
  AdminProfileAvatar,
  AdminTabButton,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminLinkAction,
  AdminSupportingText,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  adminDetailTabContentClassName,
} from '../shared/adminUx';
import {
  getBusinessSubscriptionByBusinessId,
  formatSubscriptionDate,
  formatCurrentPeriod,
  formatPaymentMethod,
  formatMonthlyAmountWithCurrency,
  hasPaymentMethod,
  getQuotePlanAllowance,
  getQuoteRemaining,
  formatQuotePlanAllowance,
} from './businessSubscriptionsData';
import BusinessBillingTransactionsTab from './BusinessBillingTransactionsTab';
import BusinessBillingHistoryTab from './BusinessBillingHistoryTab';
import BusinessBillingAuditLogTab from './BusinessBillingAuditLogTab';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'history', label: 'Subscription History' },
  { id: 'audit-log', label: 'Audit Log' },
];

const OverviewField = ({ label, children }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue>{children}</AdminDetailFieldValue>
  </div>
);

const OverviewBadgeField = ({ label, value }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {value ? (
      <div>
        <AdminStatusBadge status={value} label={value} />
      </div>
    ) : (
      <AdminDetailFieldValue>—</AdminDetailFieldValue>
    )}
  </div>
);

/**
 * Screen 2 — Business Billing Record.
 * Header remains visible across all tabs
 * (Overview / Transactions / Subscription History / Audit Log).
 */
const BusinessBillingRecord = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');

  const subscription = useMemo(() => {
    if (location.state?.subscription) return location.state.subscription;
    return getBusinessSubscriptionByBusinessId(businessId);
  }, [businessId, location.state]);

  if (!subscription) {
    return (
      <AdminLayout>
        <DashboardMainContainer className="!bg-white px-6 py-5">
          <div className="text-center space-y-4 py-16">
            <AdminAlertText>Business billing record not found</AdminAlertText>
            <AdminHeaderAction
              className="!h-[36px] !px-4"
              onClick={() => navigate('/admin/billing/transactions')}
            >
              Back to Billing & Subscriptions
            </AdminHeaderAction>
          </div>
        </DashboardMainContainer>
      </AdminLayout>
    );
  }

  const nextBillingDisplay = formatSubscriptionDate(subscription.nextBillingDate);
  const isTrial = subscription.subscriptionStatus === 'Trial';
  const isGracePeriod = subscription.subscriptionStatus === 'Grace Period';
  const hasScheduledChange =
    subscription.scheduledChangeType && subscription.scheduledChangeType !== 'None';
  const showPaymentMethodDetails = hasPaymentMethod(subscription);
  const tabContentClassName = adminDetailTabContentClassName(activeTab);
  const quoteAllowance = getQuotePlanAllowance(subscription);
  const quoteRemaining = getQuoteRemaining(subscription);
  const quoteResetsDisplay = isGracePeriod
    ? '—'
    : formatSubscriptionDate(subscription.quoteResetsOn);

  const openBusinessQuotes = () => {
    navigate('/admin/quotes/all', {
      state: {
        businessId: subscription.businessId,
        businessName: subscription.businessName,
        from: location.pathname,
      },
    });
  };

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <div className="flex items-center gap-4">
              <AdminProfileAvatar
                src={subscription.businessLogo || Profile}
                alt={subscription.businessName}
                onError={(e) => {
                  e.target.src = Profile;
                }}
              />
              <div className="flex-1 min-w-0">
                <AdminPanelTitle>{subscription.businessName}</AdminPanelTitle>
              </div>
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-8 overflow-x-auto">
                {TABS.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>

            <div className={tabContentClassName}>
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Column 1 — Subscription Details */}
                  <div className="space-y-4">
                    <OverviewBadgeField
                      label="Current Plan"
                      value={subscription.currentPlan}
                    />
                    <OverviewField label="Subscription Status">
                      {subscription.subscriptionStatus || '—'}
                    </OverviewField>
                    <OverviewField label="Current Subscription Start Date">
                      {formatSubscriptionDate(subscription.currentSubscriptionStartDate)}
                    </OverviewField>
                    <OverviewField label="Current Period">
                      {formatCurrentPeriod(subscription)}
                    </OverviewField>
                    {isTrial && (
                      <OverviewField label="Trial End Date">
                        {formatSubscriptionDate(subscription.trialEndDate)}
                      </OverviewField>
                    )}
                    {isGracePeriod && (
                      <OverviewField label="Grace Period End Date">
                        {formatSubscriptionDate(subscription.gracePeriodEndDate)}
                      </OverviewField>
                    )}
                  </div>

                  {/* Column 2 — Billing Details */}
                  <div className="space-y-4">
                    <OverviewField label="Monthly Amount">
                      {formatMonthlyAmountWithCurrency(subscription.monthlyAmount)}
                    </OverviewField>
                    <OverviewField label="GST Treatment">
                      {subscription.gstTreatment || '—'}
                    </OverviewField>
                    <OverviewField label="Next Billing Date">
                      {nextBillingDisplay}
                    </OverviewField>
                    <OverviewField label="Last Payment Date">
                      {formatSubscriptionDate(subscription.lastPaymentDate)}
                    </OverviewField>
                    <OverviewBadgeField
                      label="Last Payment Status"
                      value={subscription.lastPaymentStatus}
                    />
                  </div>

                  {/* Column 3 — Payment Method */}
                  <div className="space-y-4">
                    <OverviewField label="Card">
                      {formatPaymentMethod(subscription)}
                    </OverviewField>
                    {showPaymentMethodDetails ? (
                      <OverviewField label="Expiry">
                        {subscription.paymentExpiry || '—'}
                      </OverviewField>
                    ) : null}
                    <OverviewField label="Provider">
                      {subscription.paymentProvider || 'Stripe'}
                    </OverviewField>
                    <OverviewField label="Stripe Account ID">
                      {subscription.stripeCustomerId || '—'}
                    </OverviewField>
                  </div>

                  {/* Column 4 — Scheduled Change + Quote delivery allowance */}
                  <div className="space-y-4">
                    <OverviewField label="Change Type">
                      {hasScheduledChange ? subscription.scheduledChangeType : 'None'}
                    </OverviewField>
                    <OverviewField label="Requested At">
                      {formatSubscriptionDate(subscription.scheduledChangeRequestedAt)}
                    </OverviewField>
                    <OverviewField label="Effective At">
                      {formatSubscriptionDate(subscription.scheduledChangeEffectiveAt)}
                    </OverviewField>
                    {isGracePeriod ? (
                      <AdminSupportingText className="text-mediumgray">
                        Carried allowance from the previous paid cycle
                      </AdminSupportingText>
                    ) : null}
                    <OverviewField label="Plan Allowance">
                      {quoteAllowance ? formatQuotePlanAllowance(subscription) : '—'}
                    </OverviewField>
                    <OverviewField label="Used This Cycle">
                      {subscription.quoteUsedThisCycle ?? 0}
                    </OverviewField>
                    <OverviewField label="Remaining">{quoteRemaining}</OverviewField>
                    <OverviewField label="Resets On">{quoteResetsDisplay}</OverviewField>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Quotes</AdminDetailFieldLabel>
                      <AdminLinkAction
                        type="button"
                        onClick={openBusinessQuotes}
                        allowWhenRestricted
                      >
                        View Quotes
                      </AdminLinkAction>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'transactions' && (
                <BusinessBillingTransactionsTab businessId={subscription.businessId} />
              )}

              {activeTab === 'history' && (
                <BusinessBillingHistoryTab businessId={subscription.businessId} />
              )}

              {activeTab === 'audit-log' && (
                <BusinessBillingAuditLogTab businessId={subscription.businessId} />
              )}
            </div>
          </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BusinessBillingRecord;
